import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { searchAll, searchTypeLabel, type SearchResult } from "@/utils/search";

interface SearchBoxProps {
  variant?: "header" | "hero";
}

export default function SearchBox({ variant = "header" }: SearchBoxProps) {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const results = useMemo<SearchResult[]>(() => (keyword.trim() ? searchAll(keyword).slice(0, 6) : []), [keyword]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const submit = (value: string) => {
    const q = value.trim();
    if (!q) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <div ref={boxRef} className="relative w-full">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit(keyword);
        }}
        className={`flex items-center gap-2 rounded-full border bg-background-50 px-4 transition-colors ${
          variant === "hero"
            ? "h-12 border-transparent"
            : "h-10 border-background-200 focus-within:border-primary-300"
        }`}
      >
        <i className="ri-search-line text-base text-foreground-400" />
        <input
          type="text"
          name="keyword"
          value={keyword}
          onChange={(event) => {
            setKeyword(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="搜索剧集、演员或剧情解析"
          className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
        />
        {keyword ? (
          <button
            type="button"
            onClick={() => setKeyword("")}
            aria-label="清空搜索"
            className="flex h-5 w-5 items-center justify-center text-foreground-400 transition-colors hover:text-foreground-700"
          >
            <i className="ri-close-line" />
          </button>
        ) : null}
      </form>

      {open && keyword.trim() ? (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-lg border border-background-200 bg-background-50 shadow-xl">
          {results.length > 0 ? (
            <ul className="max-h-96 overflow-y-auto py-1">
              {results.map((item) => (
                <li key={`${item.type}-${item.id}`}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      navigate(item.link);
                    }}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-background-100"
                  >
                    <span className="h-10 w-14 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img src={item.cover} alt={item.title} className="h-full w-full object-cover object-top" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground-900">{item.title}</span>
                      <span className="block truncate text-xs text-foreground-500">{item.subtitle}</span>
                    </span>
                    <span className="shrink-0 rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                      {searchTypeLabel[item.type]}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-6 text-center text-sm text-foreground-500">
              没有找到与「{keyword}」相关的内容
            </div>
          )}
          <button
            type="button"
            onClick={() => submit(keyword)}
            className="flex w-full items-center justify-center gap-1 border-t border-background-200 bg-background-100 py-2.5 text-xs font-medium text-primary-600 transition-colors hover:bg-background-200"
          >
            查看全部结果
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      ) : null}
    </div>
  );
}